import React, { useEffect, useState, useRef, useCallback } from "react";
import { pdf } from "@react-pdf/renderer";
import { Download, Loader2, RefreshCw, ZoomIn, ZoomOut, AlertCircle, FileText, Sparkles } from "lucide-react";
import { SowPdfDocument, SowModule } from "../../../../components/scoping/SowPdfDocument";
import type { SowFields } from "../../../../utils/api/endpoints/sow";

interface SowPreviewPanelProps {
  fields: SowFields;
  clientName: string;
  sowVersion: number;
  modules: SowModule[];
  isDownloading?: boolean;
  lastSavedAt?: number;
}

export const SowPreviewPanel: React.FC<SowPreviewPanelProps> = ({
  fields,
  clientName,
  sowVersion,
  modules,
  lastSavedAt
}) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [building, setBuilding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(75);
  const [downloading, setDownloading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const lastCompiledFieldsRef = useRef<string>("");
  const isInitialMount = useRef(true);

  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;

  const clientNameRef = useRef(clientName);
  clientNameRef.current = clientName;

  const sowVersionRef = useRef(sowVersion);
  sowVersionRef.current = sowVersion;

  const modulesRef = useRef(modules);
  modulesRef.current = modules;

  const buildPdf = useCallback(async () => {
    try {
      setBuilding(true);
      setError(null);
      const doc = (
        <SowPdfDocument
          fields={fieldsRef.current}
          clientName={clientNameRef.current}
          sowVersion={sowVersionRef.current}
          modules={modulesRef.current}
        />
      );
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      setPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
      lastCompiledFieldsRef.current = JSON.stringify(fieldsRef.current);
      setIsDirty(false);
    } catch (err: any) {
      console.error("Error generating SoW PDF preview:", err);
      setError(err?.message || "Failed to compile PDF preview");
    } finally {
      setBuilding(false);
    }
  }, []);

  // Initial compilation on mount
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      buildPdf();
    }
  }, [buildPdf]);

  // Detect unsaved field edits without auto-compiling while typing
  useEffect(() => {
    if (isInitialMount.current) return;
    const currentStr = JSON.stringify(fields);
    if (lastCompiledFieldsRef.current && currentStr !== lastCompiledFieldsRef.current) {
      setIsDirty(true);
    } else {
      setIsDirty(false);
    }
  }, [fields]);

  // Auto-compile only when data is saved (lastSavedAt changes)
  useEffect(() => {
    if (lastSavedAt) {
      buildPdf();
    }
  }, [lastSavedAt, buildPdf]);

  // Auto-compile only when zoom level changes
  const isZoomInitial = useRef(true);
  useEffect(() => {
    if (isZoomInitial.current) {
      isZoomInitial.current = false;
      return;
    }
    buildPdf();
  }, [zoom, buildPdf]);

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const doc = (
        <SowPdfDocument
          fields={fields}
          clientName={clientName}
          sowVersion={sowVersion}
          modules={modules}
        />
      );
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const sanitizedName = (clientName || "Client").replace(/[^a-zA-Z0-9_-]/g, "_");
      a.download = `SOW_${sanitizedName}_v${sowVersion || 1}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      console.error("Failed to download PDF:", err);
      alert("Failed to generate PDF download. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-100 border-l border-slate-200">
      {/* Top Toolbar */}
      <div className="px-4 py-2 bg-white border-b border-slate-200 flex items-center justify-between gap-3 shrink-0">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="p-1 rounded-md bg-slate-100 text-slate-600">
            <FileText size={14} />
          </div>
          <span className="text-xs font-semibold text-slate-800 tracking-tight whitespace-nowrap">
            PDF Live Preview
          </span>

          {building ? (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-indigo-600 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded-full">
              <Loader2 size={11} className="animate-spin text-indigo-600" />
              Compiling...
            </span>
          ) : isDirty ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-800 border border-amber-200/80">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
              Changes pending
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200/70">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              Up to date
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Zoom controls */}
          <div className="flex items-center bg-slate-100/80 rounded-lg p-0.5 border border-slate-200/80">
            <button
              type="button"
              onClick={() => setZoom((z) => Math.max(50, z - 10))}
              className="p-1 text-slate-500 hover:text-slate-800 rounded transition cursor-pointer"
              title="Zoom out"
            >
              <ZoomOut size={13} />
            </button>
            <span className="text-[11px] font-mono font-medium text-slate-600 px-1.5 min-w-[36px] text-center select-none">
              {zoom}%
            </span>
            <button
              type="button"
              onClick={() => setZoom((z) => Math.min(150, z + 10))}
              className="p-1 text-slate-500 hover:text-slate-800 rounded transition cursor-pointer"
              title="Zoom in"
            >
              <ZoomIn size={13} />
            </button>
          </div>

          {/* Re-compile / Refresh Button */}
          <button
            type="button"
            onClick={buildPdf}
            disabled={building}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 cursor-pointer border ${
              isDirty
                ? "bg-amber-50/90 text-amber-900 border-amber-300 hover:bg-amber-100 shadow-2xs"
                : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50 hover:text-slate-900 shadow-2xs"
            }`}
            title={isDirty ? "Recompile PDF preview with latest changes" : "Refresh PDF preview"}
          >
            <RefreshCw
              size={13}
              className={`text-current ${building ? "animate-spin" : isDirty ? "text-amber-600" : "text-slate-400"}`}
            />
            <span>{isDirty ? "Recompile Preview" : "Refresh"}</span>
          </button>

          {/* Download Button */}
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            className="px-3 py-1.5 bg-ctb-blue text-white text-xs font-semibold rounded-lg hover:bg-ctb-blue/90 flex items-center gap-1.5 cursor-pointer shadow-2xs transition disabled:opacity-50"
          >
            {downloading ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
            Download PDF
          </button>
        </div>
      </div>

      {/* Preview Body */}
      <div className="flex-1 overflow-hidden relative flex items-center justify-center p-4">
        {error ? (
          <div className="p-6 bg-white border border-rose-200 rounded-2xl shadow-sm max-w-md text-center space-y-2">
            <AlertCircle size={24} className="text-rose-600 mx-auto" />
            <h4 className="text-xs font-bold text-slate-900">PDF Compilation Error</h4>
            <p className="text-[11px] text-slate-500">{error}</p>
            <button
              type="button"
              onClick={buildPdf}
              className="px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-lg transition mt-2 cursor-pointer"
            >
              Retry
            </button>
          </div>
        ) : pdfUrl ? (
          <iframe
            key={pdfUrl}
            src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=1&zoom=${zoom}`}
            className="w-full h-full rounded-xl shadow-md border border-slate-300/80 bg-white"
            title="Statement of Work PDF Preview"
          />
        ) : (
          <div className="flex flex-col items-center justify-center gap-2 text-slate-400">
            <Loader2 size={24} className="animate-spin text-ctb-blue" />
            <span className="text-xs">Generating PDF Preview...</span>
          </div>
        )}
      </div>
    </div>
  );
};
